// src/services/firebase.ts
import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import {
  getFirestore,
  doc,
  getDoc,
  type Firestore,
  type DocumentReference,
  type DocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import {
  getFunctions,
  type Functions,
} from "firebase/functions";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  type Auth,
} from "firebase/auth";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from "firebase/app-check";

// 環境変数の安全な取得
const getEnvVar = (key: string): string | undefined => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
};

const isProduction = getEnvVar('NODE_ENV') === 'production';
const isDevelopment = getEnvVar('NODE_ENV') === 'development';

let firebaseError: string | null = null;

type FirebaseStatus =
  | { enabled: true; app: FirebaseApp; db: Firestore; auth: Auth; functions: Functions; appCheck: AppCheck | null }
  | { enabled: false; reason: string };

/** window.FIREBASE_CONFIG または <script id="firebase-config"> から設定取得 */
function readFirebaseConfig(): Record<string, unknown> | null {
  console.log('🔍 [DEBUG] Firebase設定を読み込み中...');
  
  const g: any = globalThis as any;
  if (g && g.FIREBASE_CONFIG) {
    console.log('🔍 [DEBUG] window.FIREBASE_CONFIG から設定を取得');
    console.log('🔍 [DEBUG] 設定内容:', g.FIREBASE_CONFIG);
    return g.FIREBASE_CONFIG;
  }

  const el = document.getElementById("firebase-config") as HTMLScriptElement | null;
  if (el?.type === "application/json" && el.textContent?.trim()) {
    console.log('🔍 [DEBUG] script#firebase-config から設定を取得');
    try {
      const config = JSON.parse(el.textContent);
      console.log('🔍 [DEBUG] 設定内容:', config);
      return config;
    } catch (error) {
      console.error('❌ [DEBUG] Firebase設定のJSON解析エラー:', error);
      /* JSON壊れている場合は null を返す */
    }
  }
  
  console.error('❌ [DEBUG] Firebase設定が見つかりません');
  return null;
}

let _status: FirebaseStatus | null = null;

/** Firebase 初期化本体 */
async function _init(): Promise<FirebaseStatus> {
  console.log('🚀 [DEBUG] Firebase初期化開始');
  
  const cfg = readFirebaseConfig();
  const need = ["apiKey", "authDomain", "projectId", "appId"] as const;
  const missing: string[] = [];

  for (const k of need) {
    // @ts-ignore
    if (!cfg?.[k]) missing.push(k);
  }

  if (!cfg || missing.length) {
    firebaseError =
      `The following Firebase environment variables are not set: ` +
      (missing.length ? missing.join(", ") : need.join(", "));
    console.error('❌ [DEBUG] Firebase初期化失敗:', firebaseError);
    return { enabled: false, reason: firebaseError };
  }

  try {
    console.log('🔧 [DEBUG] Firebase設定でアプリ初期化中...');
    
    // 既存のアプリがあれば取得、なければ初期化
    const app = getApps().length ? getApp() : initializeApp({
      apiKey: String((cfg as any).apiKey),
      authDomain: String((cfg as any).authDomain),
      projectId: "wine-sommelier-v2", // 固定でプロジェクトIDを設定
      appId: String((cfg as any).appId),
      storageBucket: (cfg as any).storageBucket ? String((cfg as any).storageBucket) : undefined,
      messagingSenderId: (cfg as any).messagingSenderId ? String((cfg as any).messagingSenderId) : undefined,
      measurementId: (cfg as any).measurementId ? String((cfg as any).measurementId) : undefined
    });

    console.log('📊 [DEBUG] Firestore初期化中...');
    // エミュレータ分岐：デフォルトは本番Firebaseを使用
    const useEmulator = false; // 常に本番Firebaseを使用
    
    console.log('🔧 [DEBUG] エミュレータ使用設定:', useEmulator);
    
    const db = getFirestore(app);
    
    console.log('🔧 [DEBUG] Functions初期化中...');
    const functions = getFunctions(app, "asia-northeast1");

    console.log('🔐 [DEBUG] Firebase Auth初期化中...');
    const auth = getAuth(app);
    // 認証永続化を有効化（リロード後もログイン状態を保持）
    setPersistence(auth, browserLocalPersistence).catch(() => {});

    let appCheck: AppCheck | null = null;
    const recaptchaSiteKey =
      (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY ||
      (cfg as any).recaptchaSiteKey;
    if (recaptchaSiteKey) {
      try {
        // 開発時のデバッグトークン利用を許可（コンソール登録が必要）
        if ((import.meta as any).env?.DEV) {
          (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
            (import.meta as any).env?.VITE_APPCHECK_DEBUG_TOKEN || true;
        }
        appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaV3Provider(String(recaptchaSiteKey)),
          isTokenAutoRefreshEnabled: true,
        });
        console.log('🛡️ [DEBUG] App Check 初期化完了');
      } catch (appCheckError) {
        console.warn('⚠️ [DEBUG] App Check 初期化失敗:', appCheckError);
      }
    } else {
      console.warn('⚠️ [DEBUG] VITE_RECAPTCHA_SITE_KEY 未設定のため App Check をスキップ');
    }
    
    console.log('🌐 [DEBUG] 本番Firebaseを使用します');

    console.log('✅ [DEBUG] Firebase初期化完了');
    console.log('📋 [DEBUG] 接続先プロジェクト:', "wine-sommelier-v2");
    console.log('🌐 [DEBUG] Auth Domain:', String((cfg as any).authDomain));
    console.log('🔧 [DEBUG] エミュレータ使用:', useEmulator);
    
    firebaseError = null;
    return { enabled: true, app, db, auth, functions, appCheck };
  } catch (e: any) {
    firebaseError = `Failed to initialize Firebase: ${e?.message ?? String(e)}`;
    console.error('❌ [DEBUG] Firebase初期化エラー:', firebaseError);
    return { enabled: false, reason: firebaseError };
  }
}

async function ensureStatus(): Promise<FirebaseStatus> {
  if (_status == null) _status = await _init();
  return _status;
}

/** 外部から初期化を明示的に呼びたい場合 */
async function initFirebase(): Promise<FirebaseStatus> {
  return await ensureStatus();
}

/** 有効な場合のみ各サービスを export */
let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let auth: Auth | null = null;
let functions: Functions | null = null;
let appCheck: AppCheck | null = null;

// 初期化を遅延実行
(async () => {
  try {
    const st = await ensureStatus();
    if (st.enabled) {
      app = st.app;
      db = st.db;
      auth = st.auth;
      functions = st.functions;
      appCheck = st.appCheck;
    }
  } catch (error) {
    console.error('Firebase初期化エラー:', error);
  }
})();

// エラーハンドリングを強化したFirestore操作関数
async function ensureFirestoreOnline(timeout = 10000): Promise<void> {
  console.log("Firestore network management is currently disabled");
  // 何もしない
}

async function executeWithRetry<T>(
  operation: () => Promise<T>,
  maxRetries = 3
): Promise<T> {
  let attempt = 0;
  while (true) {
    try {
      console.log(`Firestore操作実行 (試行 ${attempt + 1}/${maxRetries})`);
      return await operation();
    } catch (error: any) {
      attempt++;
      console.error(`Firestore操作失敗 (試行 ${attempt}):`, error);
      if (attempt >= maxRetries || error.code !== 'unavailable') {
        throw error;
      }
      console.warn(`Attempt ${attempt} failed. Retrying in ${attempt * 1000}ms...`);
      await new Promise(res => setTimeout(res, attempt * 1000));
    }
  }
}

async function getDocumentSafely<T = DocumentData>(
  collectionPath: string,
  docId: string,
  options: { useRetry?: boolean; maxRetries?: number } = {}
): Promise<{ data: T | null; exists: boolean; error: string | null }> {
  if (!db) {
    return { data: null, exists: false, error: "Firestore is not initialized." };
  }
  
  try {
    const docRef = doc(db, collectionPath, docId);
    let docSnap: DocumentSnapshot;
    
    if (options.useRetry) {
      docSnap = await executeWithRetry(() => getDoc(docRef), options.maxRetries);
    } else {
      await ensureFirestoreOnline();
      docSnap = await getDoc(docRef);
    }

    if (docSnap.exists()) {
      return { data: docSnap.data() as T, exists: true, error: null };
    } else {
      return { data: null, exists: false, error: null };
    }
  } catch (error: any) {
    console.error(`Error getting document ${collectionPath}/${docId}:`, error);
    return { data: null, exists: false, error: error.message || "Failed to fetch document." };
  }
}

export { 
  app, 
  db, 
  auth, 
  functions,
  appCheck,
  firebaseError, 
  initFirebase, 
  ensureFirestoreOnline,
  executeWithRetry,
  getDocumentSafely,
};